import { useCallback, useEffect, useRef, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Microphone, Stop, Waveform, Trash, Plus, X, CheckCircle, Wrench, ListChecks } from "@phosphor-icons/react";
import { toast } from "sonner";

const MIME_PREFS = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];

function pickMime() {
  if (typeof MediaRecorder === "undefined") return "";
  return MIME_PREFS.find(m => MediaRecorder.isTypeSupported?.(m)) || "";
}

function fmtClock(s) {
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/** Operative: dictate site notes → Whisper transcript → structured notes / parts / follow-ups → apply to job. */
export default function VoiceNotesCard({ entityType, entityId, onApplied }) {
  const [data, setData] = useState({ voice_notes: [], operative_notes: [], parts_used: [], follow_ups: [] });
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startedRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data: d } = await API.get(`/in-house-portal/${entityType}/${entityId}/voice-notes`);
      setData(d);
      const unapplied = (d.voice_notes || []).find(n => !n.applied_at);
      if (unapplied) setPending(p => p || unapplied);
    } catch { /* non-blocking */ }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { clearInterval(timerRef.current); recRef.current?.stream?.getTracks().forEach(t => t.stop()); }, []);

  const upload = async (blob, mime, seconds) => {
    setUploading(true);
    try {
      const fd = new FormData();
      const ext = mime.includes("mp4") ? "mp4" : mime.includes("ogg") ? "ogg" : "webm";
      fd.append("audio", blob, `voice-note.${ext}`);
      fd.append("duration_seconds", String(seconds));
      const { data: note } = await API.post(`/in-house-portal/${entityType}/${entityId}/voice-notes`, fd, { timeout: 180000 });
      setPending(note);
      toast.success("Transcribed — review and add to the job");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't transcribe the recording");
    } finally {
      setUploading(false);
    }
  };

  const start = async () => {
    if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      toast.error("Voice recording isn't supported in this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const type = rec.mimeType || mime || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        const seconds = Math.round((Date.now() - startedRef.current) / 1000);
        clearInterval(timerRef.current);
        setRecording(false);
        setElapsed(0);
        if (seconds < 1 || blob.size < 1000) { toast.error("Recording too short"); return; }
        upload(blob, type, seconds);
      };
      recRef.current = rec;
      startedRef.current = Date.now();
      rec.start(1000);
      setRecording(true);
      timerRef.current = setInterval(() => setElapsed(Math.round((Date.now() - startedRef.current) / 1000)), 500);
    } catch {
      toast.error("Microphone access was blocked — allow the microphone and try again");
    }
  };

  const stop = () => { if (recRef.current?.state === "recording") recRef.current.stop(); };

  const setS = (patch) => setPending(p => ({ ...p, structured: { ...p.structured, ...patch } }));
  const parts = pending?.structured?.parts_used || [];
  const follow = pending?.structured?.follow_ups || [];

  const apply = async () => {
    if (!pending) return;
    try {
      const s = pending.structured;
      const { data: res } = await API.post(`/in-house-portal/voice-notes/${pending.id}/apply`, {
        completion_notes: s.completion_notes, parts_used: s.parts_used.filter(p => p.name?.trim()), follow_ups: s.follow_ups.filter(f => f?.trim()),
      });
      toast.success("Added to the job");
      setPending(null);
      onApplied?.(res);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't add the note");
    }
  };

  const discard = async () => {
    if (!pending) return;
    try { await API.delete(`/in-house-portal/voice-notes/${pending.id}`); } catch { /* ignore */ }
    setPending(null);
    load();
  };

  return (
    <div className="bg-card border border-border rounded-lg p-5 space-y-4" data-testid="voice-notes-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><Waveform className="h-5 w-5" /> Voice notes</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Say what you did, what you found and any parts you used — we'll write it up for you.</p>
        </div>
        {!recording ? (
          <Button onClick={start} disabled={uploading} className="rounded-full" data-testid="voice-record-btn">
            <Microphone className="h-4 w-4 mr-1.5" weight="fill" /> {uploading ? "Transcribing…" : "Record"}
          </Button>
        ) : (
          <Button onClick={stop} variant="destructive" className="rounded-full animate-pulse" data-testid="voice-stop-btn">
            <Stop className="h-4 w-4 mr-1.5" weight="fill" /> Stop · {fmtClock(elapsed)}
          </Button>
        )}
      </div>

      {uploading && <p className="text-sm text-muted-foreground" data-testid="voice-transcribing">Listening back and writing up your notes…</p>}

      {pending && (
        <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 space-y-3" data-testid="voice-review">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Review before adding</p>
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">What we heard ({pending.duration_seconds ? fmtClock(pending.duration_seconds) : "transcript"})</summary>
            <p className="mt-1 whitespace-pre-wrap text-slate-700 dark:text-slate-300" data-testid="voice-transcript">{pending.transcript}</p>
          </details>
          <div>
            <label className="text-xs font-medium">Completion notes</label>
            <Textarea rows={4} value={pending.structured.completion_notes} onChange={e => setS({ completion_notes: e.target.value })} className="mt-1 text-sm" data-testid="voice-notes-text" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium flex items-center gap-1"><Wrench className="h-3.5 w-3.5" /> Parts used</label>
              <button type="button" onClick={() => setS({ parts_used: [...parts, { name: "", qty: 1, unit: "each", notes: "" }] })} className="text-xs text-primary inline-flex items-center gap-0.5" data-testid="voice-part-add"><Plus className="h-3 w-3" /> Add</button>
            </div>
            {parts.length === 0 && <p className="text-xs text-muted-foreground mt-1">No parts mentioned.</p>}
            <div className="space-y-1.5 mt-1">
              {parts.map((p, i) => (
                <div key={i} className="grid grid-cols-[1fr_64px_72px_28px] gap-1.5 items-center">
                  <Input value={p.name} onChange={e => setS({ parts_used: parts.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} placeholder="Part / material" className="h-8 text-sm" data-testid={`voice-part-name-${i}`} />
                  <Input type="number" step="any" value={p.qty} onChange={e => setS({ parts_used: parts.map((x, j) => j === i ? { ...x, qty: e.target.value } : x) })} className="h-8 text-sm" data-testid={`voice-part-qty-${i}`} />
                  <Input value={p.unit} onChange={e => setS({ parts_used: parts.map((x, j) => j === i ? { ...x, unit: e.target.value } : x) })} className="h-8 text-sm" />
                  <button type="button" onClick={() => setS({ parts_used: parts.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-destructive" data-testid={`voice-part-remove-${i}`}><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium flex items-center gap-1"><ListChecks className="h-3.5 w-3.5" /> Follow-up actions</label>
              <button type="button" onClick={() => setS({ follow_ups: [...follow, ""] })} className="text-xs text-primary inline-flex items-center gap-0.5" data-testid="voice-follow-add"><Plus className="h-3 w-3" /> Add</button>
            </div>
            {follow.length === 0 && <p className="text-xs text-muted-foreground mt-1">Nothing further needed.</p>}
            <div className="space-y-1.5 mt-1">
              {follow.map((f, i) => (
                <div key={i} className="flex gap-1.5 items-center">
                  <Input value={f} onChange={e => setS({ follow_ups: follow.map((x, j) => j === i ? e.target.value : x) })} className="h-8 text-sm" data-testid={`voice-follow-${i}`} />
                  <button type="button" onClick={() => setS({ follow_ups: follow.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={discard} data-testid="voice-discard"><Trash className="h-4 w-4 mr-1" /> Discard</Button>
            <Button size="sm" onClick={apply} data-testid="voice-apply"><CheckCircle className="h-4 w-4 mr-1" /> Add to job</Button>
          </div>
        </div>
      )}

      {(data.operative_notes.length > 0 || data.parts_used.length > 0 || data.follow_ups.length > 0) && (
        <div className="space-y-3 text-sm" data-testid="voice-applied">
          {data.operative_notes.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Site notes</p>
              <ul className="space-y-1.5">
                {data.operative_notes.map(n => <li key={n.id} className="rounded-md bg-muted/40 p-2.5 whitespace-pre-wrap" data-testid={`applied-note-${n.id}`}>{n.text}<span className="block text-[10px] text-muted-foreground mt-1">{new Date(n.at).toLocaleString("en-GB")}</span></li>)}
              </ul>
            </div>
          )}
          {data.parts_used.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Parts used</p>
              <ul className="divide-y divide-border rounded-md border border-border">
                {data.parts_used.map(p => <li key={p.id} className="flex justify-between px-2.5 py-1.5" data-testid={`applied-part-${p.id}`}><span>{p.name}{p.notes ? <span className="text-muted-foreground"> — {p.notes}</span> : null}</span><span className="tabular-nums text-muted-foreground">{p.qty} {p.unit}</span></li>)}
              </ul>
            </div>
          )}
          {data.follow_ups.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Follow-ups for the office</p>
              <ul className="list-disc pl-5 space-y-0.5">{data.follow_ups.map(f => <li key={f.id} className={f.done ? "line-through text-muted-foreground" : ""}>{f.text}</li>)}</ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
